import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import React, { useEffect, useState } from 'react';
import { generatePath, Link, useNavigate } from 'react-router-dom';

import AsyncLoader from '~components/AsyncLoader';
import EmptyState from '~components/EmptyState';
import { deleteContactFlow, getContactFlows } from '~pages/SipConfiguration/api';
import ContactFlowsCard from '~pages/SipConfiguration/ContactFlows/ContactFlowsCard/index';
import { ContactFlow } from '~pages/SipConfiguration/domain';
import { useNotification } from '~providers/NotificationProvider';
import Routes from '~providers/RouteProvider/Routes';
import { APIError, UnsupportedStructureError } from '~services/Errors';

type Error = {
  text: string;
  subText: string;
};

const ContactFlows = () => {
  const navigate = useNavigate();
  const { pushNotification } = useNotification();
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [contactFlows, setContactFlows] = useState<ContactFlow[]>([]);

  const load = async () => {
    try {
      setContactFlows(await getContactFlows());
    } catch (e) {
      handleError(e);
    } finally {
      setPageLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleError = (e: any) => {
    if (e instanceof APIError) {
      setError({ text: 'Unable to request data from backend', subText: e.message });
    }

    if (e instanceof UnsupportedStructureError) {
      setError({ text: 'Data from backend Invalid', subText: 'Unable to decode response' });
    }
  };

  const deleteContactFlowFn = async (contactFlow: ContactFlow, skipArchive: boolean) => {
    try {
      await deleteContactFlow(contactFlow.contactFlowId, skipArchive);
      load();
      pushNotification('success', `Deleted contact flow ${contactFlow.name}`);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);
    }
  };

  const noResults = contactFlows.length === 0;
  const errorDisplay = error ? <EmptyState type='error' text={error.text} subText={error.subText} /> : null;

  return (
    <AsyncLoader isLoading={!pageLoaded} error={errorDisplay}>
      {noResults && (
        <EmptyState
          type='no-items-3'
          text='No Contact Flows currently exist'
          subText='Create a Contact Flow by clicking the button below'
          action={() => navigate(Routes.sipContactFlowCreate.path)}
          actionText='Create Contact Flow'
        />
      )}

      {!noResults && (
        <Grid container spacing={1} justifyContent='flex-end'>
          <Grid item style={{ display: 'flex', alignItems: 'center' }} xs={12} md={3}>
            <Button
              component={Link}
              to={Routes.sipContactFlowCreate.path}
              variant='contained'
              color='primary'
              disableElevation
              fullWidth
              startIcon={<AddIcon />}>
              Create Contact Flow
            </Button>
          </Grid>

          <Grid item xs={12}>
            <List>
              {contactFlows.map((item) => (
                <ContactFlowsCard
                  key={item.contactFlowId}
                  title={item.name}
                  onEdit={() => {
                    navigate(
                      generatePath(Routes.sipContactFlowDetails.path, {
                        contactFlowId: item.contactFlowId.toString(),
                      }),
                    );
                  }}
                  onDelete={(skipArchive) => deleteContactFlowFn(item, skipArchive)}
                />
              ))}
            </List>
          </Grid>
        </Grid>
      )}
    </AsyncLoader>
  );
};

export default ContactFlows;
